/**
 * The rendered invoice, fetched once and reused three ways.
 *
 * THIS HOOK NEVER BUILDS A DOCUMENT. Every invoice in the system is produced by
 * backend/packages/invoice (docs/INVOICE_DESIGN.md); this fetches those bytes
 * and hands them to a frame, a download and a print job, so what the operator
 * looks at, saves and prints are the same file the client receives. Drawing a
 * stand-in here would make the dashboard a second renderer, which
 * `scripts/check-invoice-boundary.js` fails the build over.
 *
 * The fetch goes through the shared axios instance because the PDF route needs
 * the Authorization header - an <iframe src="…/pdf"> would be an
 * unauthenticated request and render a 401 page inside the frame.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { b2bApi } from '../../services/b2bApi'
import { b2bErrorMessage } from './errorCopy'

export interface InvoiceDocument {
  /** Object URL for the frame, or null while there is nothing to show. */
  url: string | null
  filename: string | null
  loading: boolean
  error: string
  download: () => void
  print: () => void
  reload: () => void
}

export function useInvoiceDocument(invoiceId: string | undefined): InvoiceDocument {
  const [url, setUrl] = useState<string | null>(null)
  const [filename, setFilename] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)

  // Revoking on unmount matters here: a PDF is megabytes, and an operator who
  // opens twenty invoices in a shift would otherwise hold all of them.
  const currentUrl = useRef<string | null>(null)

  const load = useCallback(async (id: string) => {
    setLoading(true)
    setError('')
    try {
      const result = await b2bApi.getInvoicePdf(id)
      const objectUrl = URL.createObjectURL(result.blob)
      if (currentUrl.current) URL.revokeObjectURL(currentUrl.current)
      currentUrl.current = objectUrl
      return { url: objectUrl, filename: result.filename }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!invoiceId) return undefined
    let cancelled = false

    load(invoiceId)
      .then((result) => {
        if (cancelled) return
        setUrl(result.url)
        setFilename(result.filename)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setError(b2bErrorMessage(err, 'loading the invoice document'))
      })

    return () => {
      cancelled = true
    }
  }, [invoiceId, attempt, load])

  useEffect(
    () => () => {
      if (currentUrl.current) URL.revokeObjectURL(currentUrl.current)
      currentUrl.current = null
    },
    [],
  )

  const download = useCallback(() => {
    if (!url) return
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename || 'invoice.pdf'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  }, [filename, url])

  /**
   * Print through a hidden iframe pointed at the same bytes, so the printed
   * copy cannot differ from the one on screen. If the frame is blocked, the
   * document opens in a tab and the browser's own PDF viewer takes over rather
   * than the button doing nothing.
   */
  const print = useCallback(() => {
    if (!url) return
    const frame = document.createElement('iframe')
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0'
    frame.src = url
    frame.onload = () => {
      try {
        frame.contentWindow?.focus()
        frame.contentWindow?.print()
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
      window.setTimeout(() => {
        if (frame.parentNode) document.body.removeChild(frame)
      }, 60000)
    }
    document.body.appendChild(frame)
  }, [url])

  const reload = useCallback(() => setAttempt((value) => value + 1), [])

  return { url, filename, loading, error, download, print, reload }
}
